/**
 * `PageProductHeaderLinkAuthFlow` is a wrapper component around `anchor tag`.
 *
 * It uses the `useSearchParamOrDefaultProps` hook to extract analytics-related parameters, either from the search params or from the provided default options.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.to - The destination URL or route.
 * @param {('signIn', 'signUp')} [props.modalType] - Type of modal triggered in Phoenix like sign up or sign in.
 * @param {('article', 'video', 'page')} [props.contentType] - Content type of the top page like article, video, page (e.g., landing page).
 * @param {string} [props.target='_top'] - The target frame for the link. Default is '_top'.
 * @param {string|JSX.Element} props.children - The text or elements to be rendered within the link.
 *
 * @returns {JSX.Element} The rendered JSX element.
 */
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import useSearchParamOrDefaultProps from '../hooks/useSearchParamOrDefaultProps.js';
import styles from './PageProductHeaderLinkAuthFlow.module.css';
import $t from '../utils/analyticsEventBus.js';
import triggerTopPageAuthenticationFlow from '../utils/triggerTopPageAuthenticationFlow.js';

const PageProductHeaderLinkAuthFlow = ({
  target,
  children,
  fontFamily,
  fontWeight,
  color,
  backgroundColor,
  borderRadius,
  borderWidth,
  borderStyle,
  borderColor,
  margin,
  padding,
  whiteSpace,
  letterSpacing,
  minWidth,
  minHeight,
  fontSize,
  lineHeight,
  textTransform,
  height,
  alignSelf,
  eventType = 'phx_zephr_click',
  width,
  display,
  className,
  analyticFlowPrefix,
  product,
  productSku,
  modalType,
  textAlign = 'center',
  contentType,
  boxShadow,
  flexGrow,
  flexShrink,
  flexBasis,
  id,
  alignItems,
  justifyContent,
}) => {
  const { windowTopLocationHref = window.location.href, offerGroup } =
    useSearchParamOrDefaultProps();

  useEffect(() => {
    $t('register', eventType, {
      allowDuplicate: true,
      remote: true,
    });
  }, []);

  const style = {
    color,
    backgroundColor,
    borderRadius,
    borderColor,
    borderStyle,
    borderWidth,
    fontFamily,
    margin,
    padding,
    whiteSpace,
    fontWeight,
    letterSpacing,
    minWidth,
    minHeight,
    fontSize,
    lineHeight,
    textTransform,
    height,
    alignSelf,
    width,
    display,
    textAlign,
    boxShadow,
    flexGrow,
    flexShrink,
    flexBasis,
    alignItems,
    justifyContent,
  };

  return (
    <a
      onClick={(e) => {
        e.preventDefault();

        triggerTopPageAuthenticationFlow({
          analyticFlowPrefix,
          href: windowTopLocationHref,
          product,
          productSku,
          modalType,
          contentType,
          offerGroup,
        });
      }}
      id={id}
      target={target}
      className={cx(styles.link, className)}
      style={style}
    >
      {children}
    </a>
  );
};

PageProductHeaderLinkAuthFlow.displayName = 'PageProductHeaderLinkAuthFlow';

PageProductHeaderLinkAuthFlow.propTypes = {
  analyticFlowPrefix: PropTypes.string,
  to: PropTypes.string,
  target: PropTypes.string,
  children: PropTypes.node,
  fontFamily: PropTypes.string,
  color: PropTypes.string,
  backgroundColor: PropTypes.string,
  borderRadius: PropTypes.string,
  borderWidth: PropTypes.string,
  borderStyle: PropTypes.string,
  borderColor: PropTypes.string,
  boxShadow: PropTypes.string,
  text: PropTypes.string,
  margin: PropTypes.string,
  padding: PropTypes.string,
  whiteSpace: PropTypes.string,
  fontWeight: PropTypes.number,
  letterSpacing: PropTypes.string,
  minWidth: PropTypes.string,
  minHeight: PropTypes.string,
  alignSelf: PropTypes.string,
  eventType: PropTypes.string,
  fontSize: PropTypes.string,
  lineHeight: PropTypes.string,
  textTransform: PropTypes.string,
  height: PropTypes.string,
  width: PropTypes.string,
  display: PropTypes.string,
  className: PropTypes.string,
  flexGrow: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  flexShrink: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  flexBasis: PropTypes.string,
  product: PropTypes.oneOf([
    'pro',
    'investingClub',
    'registeredOnly',
    'plus',
    'allAccess',
  ]),
  productSku: PropTypes.oneOf([
    'PRO',
    'InvestingClub',
    'RegisteredOnly',
    'Plus',
    'ProICBundle',
  ]),
  contentType: PropTypes.oneOf(['article', 'video', 'page']),
  modalType: PropTypes.oneOf(['signIn', 'signUp']),
  textAlign: PropTypes.oneOf(['unset', 'left', 'center', 'right']),
  analyticFlow: PropTypes.string,
  alignItems: PropTypes.string,
  justifyContent: PropTypes.string,
  id: PropTypes.string,
};

PageProductHeaderLinkAuthFlow.defaultProps = {
  to: '',
  target: '_top',
  children: null,
  textAlign: 'unset',
};

export default PageProductHeaderLinkAuthFlow;
